import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { or, not } from 'ember-truth-helpers';
import { service } from '@ember/service';
import { action } from '@ember/object';
import { fn } from '@ember/helper';
import { t } from 'ember-intl';
import autoFocus from '../modifiers/auto-focus.js';
import autoShowModal from '../modifiers/auto-show-modal.js';
import ChipsInput from './chips-input.gjs';
import ContextMenu from './context-menu.gjs';

export default class EditUserDialog extends Component {
  @service intl;
  @tracked username = this.args.model?.username || '';
  @tracked roles = [...(this.args.model?.roles || [])];

  predefinedRoles = ['Administrator', 'GlobalWriter', 'GlobalReader'];

  get usernameExists() {
    const username = this.username;
    if (username === '' || username === this.args.model?.username) {
      return false;
    }

    const locale = this.intl.primaryLocale;
    return this.args.users.some(
      (x) =>
        x.username.localeCompare(username, locale, { sensitivity: 'base' }) ===
        0,
    );
  }

  @action addRole(role) {
    if (!this.roles.includes(role)) {
      this.roles = [...this.roles, role];
    }
  }

  @action removeRole(index) {
    this.roles = this.roles.filter((x, i) => i !== index);
  }

  @action onUsernameInput(event) {
    this.username = event.target.value;
  }

  @action onSubmit(e) {
    this.args.onSubmit({
      username: this.username,
      roles: this.roles,
    });
  }

  <template>
    <dialog
      class="modal"
      {{autoShowModal}}
      {{on "cancel" @onClose}}
      {{on "close" @onClose}}
    >
      <form
        aria-label="modal"
        method="dialog"
        class="modal-box"
        {{on "submit" this.onSubmit}}
      >
        <h3 class="text-lg font-bold mb-2">
          {{t (if @model "users.edit" "users.new")}}
        </h3>

        <fieldset class="fieldset w-full">
          <legend class="fieldset-legend">{{t "users.username"}}</legend>
          <input
            type="text"
            name="username"
            placeholder={{t "users.username-placeholder"}}
            class="input input-bordered w-full
              {{if this.usernameExists 'input-error'}}"
            value={{this.username}}
            {{on "input" this.onUsernameInput}}
            {{autoFocus}}
          />
        </fieldset>

        <fieldset class="fieldset w-full">
          <legend class="fieldset-legend pb-0">
            {{t "users.roles"}}
            <ContextMenu as |Item|>
              {{#each this.predefinedRoles as |role|}}
                <Item
                  @action={{fn this.addRole role}}
                  @label={{role}}
                />
              {{/each}}
            </ContextMenu>
          </legend>

          <ChipsInput
            class="w-full"
            @items={{this.roles}}
            @addItem={{this.addRole}}
            @removeItem={{this.removeRole}}
          />
        </fieldset>

        <div class="modal-action">
          <button
            type="submit"
            class="btn btn-primary uppercase"
            disabled={{or (not this.username) this.usernameExists}}
          >
            {{t (if @model "update" "create")}}
          </button>
        </div>
      </form>

      <form aria-label="backdrop" method="dialog" class="modal-backdrop">
        <button type="submit" />
      </form>
    </dialog>
  </template>
}
